import { ChangeEvent, FC, useRef, useState } from "react"
import { Avatar, Box, Button, Stack, Typography } from "@mui/material"
import { PhotoCamera } from "@mui/icons-material"
import { SnackbarAlert } from "@/core/components"
import { useGuestBotWriteAccess } from "../../hooks"
import {
  useGetProfilePhotoQuery,
  useSetProfilePhotoMutation,
} from "../../store"

interface IProps {
  botId: string
}

const MAX_SIZE = 5 * 1024 * 1024 // 5 MiB

/**
 * Просмотр и загрузка фото профиля живого бота.
 * Фото не хранится в БД — ссылка запрашивается каждый раз (getFile) и временна.
 */
export const GuestBotPhotoManager: FC<IProps> = ({ botId }) => {
  const canWrite = useGuestBotWriteAccess()
  const inputRef = useRef<HTMLInputElement>(null)
  const { data, isFetching } = useGetProfilePhotoQuery({
    pathParams: { id: botId },
  })
  const [setPhoto, { isLoading: isUploading }] = useSetProfilePhotoMutation()
  const [snackbar, setSnackbar] = useState<{
    variant: "success" | "error"
    message: string
  } | null>(null)

  const handleSelect = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = "" // позволяет выбрать тот же файл повторно
    if (!file) return

    if (!file.type.startsWith("image/")) {
      setSnackbar({
        variant: "error",
        message: "Нужен файл изображения (image/*)",
      })
      return
    }
    if (file.size > MAX_SIZE) {
      setSnackbar({
        variant: "error",
        message: "Максимальный размер файла — 5 MiB",
      })
      return
    }

    const fd = new FormData()
    fd.append("photo", file)
    try {
      await setPhoto({ pathParams: { id: botId }, body: fd }).unwrap()
      setSnackbar({ variant: "success", message: "Фото профиля обновлено" })
    } catch (err) {
      const message =
        (err as { data?: { message?: string } })?.data?.message ??
        "Не удалось загрузить фото"
      setSnackbar({ variant: "error", message })
    }
  }

  return (
    <Stack spacing={2}>
      <Typography variant="subtitle1">Фото профиля</Typography>
      <Box display="flex" alignItems="center" gap={2}>
        <Avatar
          src={data?.url ?? undefined}
          sx={{ width: 72, height: 72 }}
          variant="rounded"
        >
          {!data?.url && <PhotoCamera />}
        </Avatar>
        {canWrite && (
          <Button
            variant="outlined"
            startIcon={<PhotoCamera />}
            onClick={() => inputRef.current?.click()}
            disabled={isUploading || isFetching}
          >
            {data?.url ? "Заменить фото" : "Загрузить фото"}
          </Button>
        )}
      </Box>
      <Typography variant="caption" color="text.secondary">
        Только изображения, до 5 MiB. Фото применяется к живому боту в Telegram.
      </Typography>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        style={{ display: "none" }}
        onChange={handleSelect}
      />
      <SnackbarAlert
        isOpen={!!snackbar}
        onClose={() => setSnackbar(null)}
        variant={snackbar?.variant}
        title={snackbar?.variant === "error" ? "Ошибка" : "Успешно"}
        message={snackbar?.message ?? ""}
      />
    </Stack>
  )
}
